import React from "react";
import Footer from "./Footer";
import { Crown, Cog, TrendingUp, ChevronRight, Users } from "lucide-react";

// Small inline LinkedIn glyph (not every lucide-react version ships this icon,
// so it's drawn directly to avoid an import error).


/**
 * Leadership section — Axtelica
 * Rebuilt to match the reference design: dark radial-gradient banner with
 * a wave/dot backdrop, then a stack of leadership cards (photo circle +
 * icon badge + role + name + bio + expertise list), closing with a dark
 * "purpose" banner.
 *
 * Swap `avatar` for a real <img src="..."> once photos are available —
 * for now each card renders an initials avatar in the role's accent color.
 */

const team = [
  {
    role: "Chief Executive Officer (CEO)",
    name: "Niladri Mahapatra",
    icon: Crown,
    accent: "#D4AF37", // champagne gold
    bio: [
      "As the Chief Executive Officer of Axtelica, Niladri Mahapatra leads the company's vision of helping organizations harness the power of Artificial Intelligence to drive innovation, operational excellence, and sustainable business growth. With over 25 years of industry experience, he brings deep expertise in Artificial Intelligence, product strategy, Agile transformation, workflow automation, and enterprise digital transformation.",
      "Niladri specializes in designing and delivering AI-powered business solutions that enable organizations to automate processes, improve decision-making, and accelerate digital adoption. His experience spans strategic consulting, AI implementation, product innovation, and enterprise modernization across diverse industries.",
      "Passionate about making AI practical and accessible, Niladri works closely with business leaders to identify high-impact opportunities where intelligent automation and AI can create measurable business value. He also mentors organizations and professionals through corporate training and executive workshops, helping them confidently adopt emerging technologies and build future-ready capabilities.",
      "Under his leadership, Axtelica is committed to transforming ideas into intelligent business solutions\u2014combining advanced AI technologies with business strategy to help organizations innovate faster, operate smarter, and achieve lasting competitive advantage.",
    ],
    listHeader: "Core Expertise",
    list: [
      "AI Strategy & Adoption",
      "AI-Powered Product Development",
      "Business Process Automation",
      "Enterprise Digital Transformation",
      "Product Strategy & Innovation",
      "Agile Transformation",
      "Executive AI Consulting",
      "Corporate Training & Enablement",
    ],
  },
  {
    role: "Chief Technology Officer",
    name: "Girish A",
    icon: Cog,
    accent: "#276EF1", // electric blue
    bio: [
      "As CTO of Axtelica, Girish leads the technology vision and engineering excellence behind our AI, automation, and data-driven solutions. He focuses on building scalable, secure and intelligent platforms that help organizations solve complex problems and drive innovation.",
      "With deep expertise in AI/ML, cloud architecture, data analytics, and modern software engineering, Girish drives the adoption of emerging technologies to deliver impact at scale.",
    ],
    listHeader: "Core Expertise",
    list: [
      "AI & Machine Learning",
      "Enterprise Architecture",
      "Cloud & Scalable Infrastructure",
      "Data Analytics & BI",
      "Workflow Automation",
      "Technology Innovation",
      "Digital Product Engineering",
    ],
  },
  {
    role: "Chief Growth Officer",
    name: "Vishal D",
    icon: TrendingUp,
    accent: "#7C3AED", // accent purple
    bio: [
      "As Chief Growth Officer, Vishal drives Axtelica's growth strategy by aligning go-to-market execution, partnerships, customer success, and revenue operations. He is passionate about creating long-term value for clients through innovative AI and automation solutions.",
      "Vishal works closely with clients and partners to accelerate digital transformation and achieve measurable business outcomes.",
    ],
    listHeader: "Core Responsibilities",
    list: [
      "Go-to-Market Strategy & Positioning",
      "Revenue Operations & Pipeline Growth",
      "Strategic Partnerships",
      "Customer Success & Business Expansion",
      "AI-Native Sales Enablement",
      "Growth Analytics & Performance Optimization",
      "Market Development & Brand Strategy",
    ],
  },
];

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function Avatar({ name, accent }) {
  return (
    <div
      className="relative flex h-[220px] w-[220px] shrink-0 items-center justify-center rounded-full text-5xl font-bold text-white ring-4 ring-offset-4 ring-offset-white"
      style={{
        background: `linear-gradient(135deg, ${accent}, #060912)`,
        boxShadow: `0 20px 40px -10px ${accent}55`,
        "--tw-ring-color": `${accent}55`,
      }}
    >
      {initials(name)}
    </div>
  );
}

function TeamCard({ member }) {
  const Icon = member.icon;
  return (
    <article className="relative flex flex-col items-center gap-10 overflow-hidden rounded-[28px] border border-black/[0.03] bg-white p-10 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.05),0_4px_6px_-2px_rgba(0,0,0,0.02)] sm:p-[60px] md:flex-row md:items-start">
      {/* top gradient bar */}
      <span
        className="pointer-events-none absolute left-0 top-0 h-[6px] w-full"
        style={{ background: `linear-gradient(90deg, ${member.accent}, #D4AF37)` }}
      />

      {/* photo + linkedin */}
      <div className="flex shrink-0 flex-col items-center gap-5">
        <Avatar name={member.name} accent={member.accent} />
        
      </div>

      {/* content */}
      <div className="w-full text-center md:text-left">
        <div className="mb-3 flex items-center justify-center gap-3 md:justify-start">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-full text-white"
            style={{ backgroundColor: member.accent }}
          >
            <Icon size={18} />
          </span>
          <span
            className="text-xs font-extrabold uppercase tracking-[0.25em]"
            style={{ color: member.accent }}
          >
            {member.role}
          </span>
        </div>

        <h2 className="mb-2 text-3xl font-bold text-[#060912] sm:text-[2.2rem]">
          {member.name}
        </h2>
        <span
          className="mx-auto mb-6 block h-[3px] w-14 rounded-full md:mx-0"
          style={{ backgroundColor: member.accent }}
        />

        {member.bio.map((para, i) => (
          <p key={i} className="mb-4 text-left leading-[1.7] text-[#475569] last:mb-8">
            {para}
          </p>
        ))}

        <div
          className="mb-5 border-t pt-6 text-left text-xs font-bold uppercase tracking-[0.15em]"
          style={{ color: member.accent, borderColor: "#f1f5f9" }}
        >
          {member.listHeader}
        </div>
        <ul className="grid list-none grid-cols-1 gap-x-6 text-left sm:grid-cols-2">
          {member.list.map((item) => (
            <li
              key={item}
              className="flex items-start gap-2 border-t border-[#f1f5f9] py-[14px] text-[0.95rem] text-[#334155]"
            >
              <ChevronRight
                size={16}
                className="mt-[3px] shrink-0"
                style={{ color: member.accent }}
              />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function WaveBackdrop() {
  // Decorative dotted wave pattern echoing the reference banner
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
      viewBox="0 0 1200 450"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="waveBlue" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#276EF1" stopOpacity="0" />
          <stop offset="50%" stopColor="#276EF1" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#276EF1" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="wavePurple" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7C3AED" stopOpacity="0" />
          <stop offset="50%" stopColor="#7C3AED" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
        </linearGradient>
      </defs>
      {Array.from({ length: 14 }).map((_, row) =>
        Array.from({ length: 40 }).map((_, col) => {
          const x = col * 20;
          const yOffset = Math.sin(col * 0.35 + row * 0.6) * 18;
          const y = 40 + row * 22 + yOffset;
          const onLeft = x < 260 + Math.sin(row) * 40;
          const onRight = x > 940 - Math.sin(row) * 40;
          if (!onLeft && !onRight) return null;
          return (
            <circle
              key={`${row}-${col}`}
              cx={x}
              cy={y}
              r={1.4}
              fill={onLeft ? "url(#waveBlue)" : "url(#wavePurple)"}
            />
          );
        })
      )}
    </svg>
  );
}

export default function TeamSection() {
  return (
    <div className="bg-[#f8fafc] font-['Inter',sans-serif]">
      {/* Banner */}
      <section
        className="relative flex min-h-[350px] items-center justify-center overflow-hidden px-[8%] py-24 text-center text-white sm:py-[120px] lg:h-[450px]"
        style={{
          background:
            "radial-gradient(circle at 70% 30%, #4C1D95 0%, transparent 60%), radial-gradient(circle at 20% 80%, #276EF1 0%, transparent 50%), #060912",
        }}
      >
        <WaveBackdrop />
        <div className="relative z-10 max-w-3xl">
          <div className="mb-4 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">
            <span>&#8594;</span> Our Leadership
          </div>
          <h1 className="mb-5 text-4xl font-normal leading-tight sm:text-[3.5rem]">
            Executive Leadership
          </h1>
          <p className="mx-auto max-w-xl leading-relaxed text-white/70">
            Visionary leaders driving Axtelica&rsquo;s mission to transform ideas into
            intelligent business solutions through AI, automation and innovation.
          </p>
          <span className="mx-auto mt-8 block h-[3px] w-14 rounded-full bg-[#D4AF37]" />
        </div>
      </section>

      {/* Team Section */}
      <section className="px-[8%] py-[100px]">
        <div className="mx-auto flex max-w-[1100px] flex-col gap-10">
          {team.map((member) => (
            <TeamCard key={member.name} member={member} />
          ))}
        </div>
      </section>

      {/* Purpose banner */}
      <section className="px-[8%] pb-[100px]">
        <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-6 rounded-[24px] bg-[#060912] px-10 py-12 text-center text-white sm:flex-row sm:text-left">
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-[#D4AF37] text-[#D4AF37]">
            <Users size={28} />
          </span>
          <div>
            <h3 className="mb-2 text-2xl font-bold">
              Leadership with Purpose. Innovation with Impact.
            </h3>
            <p className="text-white/60">
              Our leadership team is committed to building an AI-first future and
              empowering organizations to innovate, automate and grow with confidence.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}